declare let gtag: Function;

import { Component, OnInit, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DossierService } from '../../services/dossier.service';
import { AuthService, KeypairData } from '../../services/auth.service';
import { CryptoService } from '../../services/crypto.service';
import { Dossier, Upload, UploadStatus, DossierStatus, SignatureStatus } from '../../models/dossier.model';
import { extractApiError } from '../../utils/api-error.utils';

@Component({
  selector: 'app-dossier-detail',
  templateUrl: './dossier-detail.component.html',
  styleUrls: ['./dossier-detail.component.css']
})
export class DossierDetailComponent implements OnInit, OnDestroy {

  dossier: Dossier | null = null;
  loading = false;
  error = '';

  uploading = false;
  uploadError = '';
  dragOver = false;

  /** DEK unlock state */
  dekUnlocked = false;
  dekUnlocking = false;
  dekPassword = '';
  dekError = '';
  /** True when the dossier requires DEK unlock (has encrypted_dek). */
  needsDekUnlock = false;
  /** True when dekError is the "no keypair" error — shows My Account link. */
  noKeypairFound = false;

  dossierId = '';

  // --- Signature dialog ---
  signatureDialogTitle: string | null = null;
  signatureDialogResult: SignatureStatus | null = null;

  /** The upload the open panel belongs to — lets the panel re-verify. */
  signatureDialogUploadId: string | null = null;

  openSignatureDialog(filename: string, uploadId: string, result: SignatureStatus): void {
    this.signatureDialogTitle = filename;
    this.signatureDialogUploadId = uploadId;
    this.signatureDialogResult = result;
  }

  closeSignatureDialog(): void {
    this.signatureDialogResult = null;
  }
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  /** Cached keypair data (two-slot format) to avoid re-fetching on each unlock attempt. */
  private keypairData: KeypairData | null = null;

  /** Downstream export state */
  exportingDossier = false;
  exportDossierMsg = '';
  exportUploadingId: string | null = null;
  exportUploadMsg: { [uploadId: string]: string } = {};
  downstreamSystemName: string | null = null;

  /** External ID (dossier reference) inline-edit state */
  editingExternalId = false;
  externalIdDraft = '';
  savingExternalId = false;
  externalIdMsg = '';

  /** Tradefinance downstream-header inputs (dossier-level). */
  tfCustomerKey = '';
  tfAccountKey = '';
  tfFromCountry = '';
  savingTfHeaders = false;
  tfHeadersMsg = '';

  /** Terminal upload statuses — no further pipeline progress expected */
  private readonly terminalStatuses: string[] = [
    UploadStatus.COMPLETED,
    UploadStatus.CLASSIFICATION_COMPLETED,
    UploadStatus.EXTRACTION_COMPLETED,
    UploadStatus.PROCESSING_FAILED,
    UploadStatus.OCR_FAILED,
    UploadStatus.CLASSIFICATION_FAILED,
    UploadStatus.EXTRACTION_FAILED,
  ];

  /** Accepted file extensions */
  readonly acceptedTypes = '*/*';
  private readonly allowedExtensions = ['pdf', 'jpg', 'jpeg', 'zip'];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dossierService: DossierService,
    private authService: AuthService,
    private cryptoService: CryptoService,
    private translate: TranslateService,
    private titleService: Title,
  ) {}

  ngOnInit(): void {
    this.dossierId = this.route.snapshot.paramMap.get('id') || '';
    if (this.dossierId) {
      this.loadDossier();
      this.loadDownstreamSystemName();
    }
  }

  private loadDownstreamSystemName(): void {
    this.dossierService.getDownstreamEndpoint().subscribe({
      next: (res) => { this.downstreamSystemName = res.system_name; },
      error: () => {},
    });
  }

  loadDossier(): void {
    this.loading = true;
    this.error = '';
    this.dossierService.getDossier(this.dossierId).subscribe({
      next: (data) => {
        this.dossier = data;
        this.titleService.setTitle(
          this.translate.instant('dossierDetail.titlePrefix') + data.external_id,
        );
        this.syncTradeFinanceDrafts();
        this.loading = false;
        this.checkDekStatus();
        this.managePoll();
      },
      error: (err) => {
        this.error = err?.error?.detail || err?.message || 'Failed to load dossier';
        this.loading = false;
      }
    });
  }

  /**
   * Check whether this dossier requires DEK unlock.
   * If encrypted_dek is present, the user must provide their password
   * before uploads or other DEK-dependent operations can proceed.
   *
   * Uses the CryptoService unlocked set to detect already-unlocked dossiers
   * and auto-unlocks transparently when a cached private key is available.
   */
  private checkDekStatus(): void {
    if (!this.dossier?.encrypted_dek) {
      // Legacy dossier or no encryption — no unlock needed
      this.needsDekUnlock = false;
      this.dekUnlocked = true;
      return;
    }

    // Already unlocked in this session?
    if (this.cryptoService.isDossierUnlocked(this.dossier.oid)) {
      this.needsDekUnlock = true;
      this.dekUnlocked = true;
      return;
    }

    // Server confirmed the dd_dek cookie has a valid-format DEK for this dossier?
    if (this.dossier.dek_available) {
      this.needsDekUnlock = true;
      this.dekUnlocked = true;
      this.cryptoService.addUnlockedDossier(this.dossier.oid);
      return;
    }

    // Cached private key available? Auto-unlock transparently
    if (this.cryptoService.hasCachedPrivateKey()) {
      this.needsDekUnlock = true;
      this.dekUnlocked = false;
      this.autoUnlockDek();
      return;
    }

    // Dossier has encryption — show unlock prompt
    this.needsDekUnlock = true;
    this.dekUnlocked = false;
    this.dekError = '';
  }

  /**
   * Reset DEK unlock state and re-show the password prompt.
   * Called when a DEK-dependent API call fails with 423 (locked),
   * indicating the cookie DEK is stale or invalid.
   */
  resetDekState(): void {
    if (!this.dossier) { return; }
    this.dekUnlocked = false;
    this.dekError = this.translate.instant('dossierDetail.dekExpired');
    this.cryptoService.removeUnlockedDossier(this.dossier.oid);
  }

  ngOnDestroy(): void {
    this.stopPoll();
    this.titleService.setTitle('Document Digitization');
  }

  /** Start or stop polling based on whether any upload is still processing. */
  private managePoll(): void {
    if (!this.dossier?.uploads?.length) {
      this.stopPoll();
      return;
    }
    const hasActive = this.dossier.uploads.some(
      (u: Upload) => !this.terminalStatuses.includes(u.status)
    );
    if (hasActive && !this.pollTimer) {
      this.pollTimer = setInterval(() => this.refreshDossier(), 5000);
    } else if (!hasActive) {
      this.stopPoll();
    }
  }

  /** Silent refresh — no loading spinner, just update data. */
  private refreshDossier(): void {
    this.dossierService.getDossier(this.dossierId).subscribe({
      next: (data) => {
        this.dossier = data;
        this.managePoll();
      },
      error: () => {
        // Silently ignore poll errors — next tick will retry
      }
    });
  }

  private stopPoll(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  goBack(): void {
    this.router.navigate(['/dossiers']);
  }

  navigateToUpload(upload: Upload): void {
    this.router.navigate(['/dossiers', this.dossierId, 'uploads', upload.oid, 'review']);
  }

  // --- File upload ---

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFiles(Array.from(input.files));
      input.value = '';
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;
    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.handleFiles(Array.from(event.dataTransfer.files));
    }
  }

  private handleFiles(files: File[]): void {
    for (const file of files) {
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      if (!this.allowedExtensions.includes(ext)) {
        this.uploadError = `Unsupported file type: .${ext}. Accepted: PDF, JPG, ZIP.`;
        return;
      }
    }
    if (this.needsDekUnlock && !this.dekUnlocked) {
      this.uploadError = this.translate.instant('dossierDetail.dekRequired');
      return;
    }
    this.uploadError = '';
    this.uploading = true;

    const upload$ = files.length === 1
      ? this.dossierService.uploadFile(this.dossierId, files[0])
      : this.dossierService.uploadFileBatch(this.dossierId, files);

    upload$.subscribe({
      next: () => {
        this.uploading = false;
        this.loadDossier();
        if (typeof gtag === 'function') {
          gtag('event', 'document_upload', { file_count: files.length });
        }
      },
      error: (err) => {
        if (err?.status === 423) {
          this.resetDekState();
        }
        this.uploadError = extractApiError(err, 'Upload failed');
        this.uploading = false;
        if (typeof gtag === 'function') {
          gtag('event', 'dd_error', { event_category: 'dossier_flow', step: 'upload', error_message: (this.uploadError || '').substring(0, 100) });
        }
      }
    });
  }

  // --- Upload actions ---

  /** Active processing statuses — delete is blocked while any of these apply. */
  private readonly activeProcessingStatuses: string[] = [
    UploadStatus.UPLOADED,
    UploadStatus.PROCESSING,
    UploadStatus.OCR_COMPLETED,
    UploadStatus.CLASSIFICATION_COMPLETED,
    UploadStatus.EXTRACTING,
  ];

  isUploadDeletable(upload: Upload): boolean {
    return !this.activeProcessingStatuses.includes(upload.status);
  }

  deleteUpload(event: Event, upload: Upload): void {
    event.stopPropagation();
    if (!confirm(this.translate.instant('dossierDetail.deleteUploadConfirm'))) { return; }
    this.dossierService.deleteUpload(this.dossierId, upload.oid).subscribe({
      next: () => this.loadDossier(),
      error: (err) => {
        this.error = err?.error?.detail || err?.message || 'Delete failed';
      }
    });
  }

  openMyAccountSecurity(): void {
    (window as any).ddShowMyAccount?.('sec');
  }

  // --- DEK unlock flow ---

  /**
   * Perform the full client-side DEK unlock:
   *   1. Fetch encrypted private key material from server
   *   2. Derive AES key from password via scrypt
   *   3. Decrypt RSA private key with AES-256-GCM
   *   4. Unwrap dossier's encrypted_dek with RSA-OAEP
   *   5. POST cleartext DEK to /account/unlock-dek (sets HttpOnly cookie)
   */
  async unlockDek(): Promise<void> {
    if (!this.dossier?.encrypted_dek || !this.dekPassword) {
      this.dekError = this.translate.instant('dossierDetail.dekPasswordRequired');
      return;
    }

    this.dekUnlocking = true;
    this.dekError = '';

    try {
      let privateKey: CryptoKey;

      // Use cached private key if available — skip password derivation
      const cached = this.cryptoService.getCachedPrivateKey();
      if (cached) {
        privateKey = cached;
      } else {
        // Step 1: Fetch keypair data (cache for retries)
        if (!this.keypairData) {
          this.keypairData = await this.authService.getKeypairData().toPromise() ?? null;
        }
        if (!this.keypairData) {
          this.dekError = this.translate.instant('dossierDetail.dekNoKeypair');
          this.noKeypairFound = true;
          this.dekUnlocking = false;
          return;
        }

        // Step 2: Select the keypair slot that wrapped this dossier's DEK
        const slot = (this.dossier.key_slot === 'B') ? this.keypairData.slot_b : this.keypairData.slot_a;
        if (!slot) {
          this.dekError = this.translate.instant('dossierDetail.dekNoKeypair');
          this.noKeypairFound = true;
          this.dekUnlocking = false;
          return;
        }

        // Step 3: Derive AES key from password via scrypt
        const derivedKey = await this.cryptoService.deriveKeyFromPassword(
          this.dekPassword,
          slot.private_key_salt,
        );

        // Step 4: Decrypt RSA private key with AES-256-GCM
        try {
          privateKey = await this.cryptoService.decryptPrivateKey(
            derivedKey,
            slot.encrypted_private_key,
            slot.private_key_nonce,
          );
        } catch {
          this.dekError = this.translate.instant('dossierDetail.dekWrongPassword');
          this.dekUnlocking = false;
          return;
        }
      }

      // Step 4: Unwrap DEK with RSA-OAEP
      const dekHex = await this.cryptoService.unwrapDek(
        privateKey,
        this.dossier.encrypted_dek,
      );

      // Step 5: POST cleartext DEK to server (sets HttpOnly cookie)
      await this.authService.unlockDek(this.dossier.oid, dekHex).toPromise();

      // Success — track in session
      this.dekUnlocked = true;
      this.dekPassword = '';
      this.dekError = '';
      this.cryptoService.addUnlockedDossier(this.dossier.oid);
    } catch (err: any) {
      this.dekError = err?.error?.detail || err?.message || this.translate.instant('dossierDetail.dekUnlockFailed');
    } finally {
      this.dekUnlocking = false;
    }
  }

  /**
   * Auto-unlock DEK transparently using the cached private key.
   * Called from checkDekStatus() when a cached key exists but the
   * dossier is not yet in the unlocked set. No password prompt shown.
   * On failure, falls back to showing the password prompt.
   */
  private async autoUnlockDek(): Promise<void> {
    if (!this.dossier?.encrypted_dek) { return; }

    const privateKey = this.cryptoService.getCachedPrivateKey();
    if (!privateKey) { return; }

    this.dekUnlocking = true;
    this.dekError = '';

    try {
      // Unwrap DEK with cached RSA private key
      const dekHex = await this.cryptoService.unwrapDek(
        privateKey,
        this.dossier.encrypted_dek,
      );

      // POST cleartext DEK to server (sets HttpOnly cookie)
      await this.authService.unlockDek(this.dossier.oid, dekHex).toPromise();

      // Success
      this.dekUnlocked = true;
      this.cryptoService.addUnlockedDossier(this.dossier.oid);
    } catch {
      // Cached key failed (e.g. different keypair) — show password prompt
      this.dekUnlocked = false;
    } finally {
      this.dekUnlocking = false;
    }
  }

  // --- Status helpers ---

  getDossierStatusColor(status: DossierStatus): string {
    switch (status) {
      case DossierStatus.CREATED:    return '#9e9e9e';
      case DossierStatus.PROCESSING: return '#1976d2';
      case DossierStatus.COMPLETED:  return '#4caf50';
      case DossierStatus.DELETED:    return '#f44336';
      default:                       return '#9e9e9e';
    }
  }

  getUploadStatusColor(status: UploadStatus): string {
    switch (status) {
      case UploadStatus.UPLOADED:                    return '#9e9e9e';
      case UploadStatus.PROCESSING:                  return '#1976d2';
      case UploadStatus.OCR_COMPLETED:               return '#42a5f5';
      case UploadStatus.CLASSIFICATION_COMPLETED:    return '#ff9800';
      case UploadStatus.EXTRACTION_COMPLETED:        return '#009688';
      case UploadStatus.COMPLETED:                   return '#4caf50';
      case UploadStatus.PROCESSING_FAILED:
      case UploadStatus.OCR_FAILED:
      case UploadStatus.CLASSIFICATION_FAILED:
      case UploadStatus.EXTRACTION_FAILED:           return '#f44336';
      default:                                       return '#9e9e9e';
    }
  }

  navigateToVerification(): void {
    this.router.navigate(['/dossiers', this.dossierId, 'verification']);
  }

  get hasOrgGroup(): boolean {
    return !!this.authService.user?.org_group;
  }

  get isTradeFinance(): boolean {
    return this.dossier?.trade_finance === true;
  }

  togglingTradeFinance = false;

  toggleTradeFinance(): void {
    if (!this.dossier || this.togglingTradeFinance) return;
    const newValue = !this.dossier.trade_finance;
    this.togglingTradeFinance = true;
    this.dossierService.updateTradeFinanceFlag(this.dossierId, newValue).subscribe({
      next: (updated) => {
        if (this.dossier) { this.dossier = { ...this.dossier, ...updated }; }
        this.togglingTradeFinance = false;
      },
      error: () => { this.togglingTradeFinance = false; },
    });
  }

  get hasExtractedDocuments(): boolean {
    if (!this.dossier?.uploads) return false;
    return this.dossier.uploads.some(u =>
      u.status === UploadStatus.EXTRACTION_COMPLETED || u.status === UploadStatus.COMPLETED
    );
  }

  exportDossier(): void {
    if (!this.dossier) { return; }
    this.exportingDossier = true;
    this.exportDossierMsg = '';
    const sysName = this.downstreamSystemName || 'Send';
    this.dossierService.exportToDownstream(this.dossierId).subscribe({
      next: (res) => {
        this.exportDossierMsg = `✓ Sent ${res.documents_sent}/${res.documents_total} documents to ${res.system_name || sysName}`;
        this.exportingDossier = false;
      },
      error: (err: { error?: { detail?: string }; message?: string }) => {
        const detail = err?.error?.detail;
        this.exportDossierMsg = detail
          ? `✗ Cannot send to ${sysName}: ${detail}`
          : `✗ Cannot send to ${sysName}`;
        this.exportingDossier = false;
      },
    });
  }

  exportUpload(event: Event, upload: Upload): void {
    event.stopPropagation();
    if (this.exportUploadingId === upload.oid) { return; }
    this.exportUploadingId = upload.oid;
    this.exportUploadMsg = { ...this.exportUploadMsg, [upload.oid]: '' };
    const sysName = this.downstreamSystemName || 'Send';
    this.dossierService.exportUploadToDownstream(this.dossierId, upload.oid).subscribe({
      next: (res) => {
        this.exportUploadMsg = { ...this.exportUploadMsg, [upload.oid]: `✓ ${res.documents_sent}/${res.documents_total} → ${res.system_name || sysName}` };
        this.exportUploadingId = null;
      },
      error: (err: { error?: { detail?: string }; message?: string }) => {
        const detail = err?.error?.detail;
        this.exportUploadMsg = {
          ...this.exportUploadMsg,
          [upload.oid]: detail
            ? `✗ Cannot send to ${sysName}: ${detail}`
            : `✗ Cannot send to ${sysName}`,
        };
        this.exportUploadingId = null;
      },
    });
  }

  startEditExternalId(): void {
    if (!this.dossier) { return; }
    this.externalIdDraft = this.dossier.external_id;
    this.externalIdMsg = '';
    this.editingExternalId = true;
  }

  cancelEditExternalId(): void {
    this.editingExternalId = false;
    this.externalIdDraft = '';
    this.externalIdMsg = '';
  }

  saveExternalId(): void {
    if (!this.dossier) { return; }
    const trimmed = this.externalIdDraft.trim();
    if (!trimmed) {
      this.externalIdMsg = '✗ Reference must not be empty';
      return;
    }
    if (trimmed === this.dossier.external_id) {
      this.cancelEditExternalId();
      return;
    }
    this.savingExternalId = true;
    this.externalIdMsg = '';
    this.dossierService.updateExternalId(this.dossierId, trimmed).subscribe({
      next: (updated: Dossier) => {
        // Merge — the PATCH response is the bare dossiers row with no
        // `uploads`; replacing this.dossier outright would drop them.
        if (this.dossier) { this.dossier = { ...this.dossier, ...updated }; }
        this.savingExternalId = false;
        this.editingExternalId = false;
      },
      error: (err: { error?: { detail?: string }; message?: string }) => {
        this.externalIdMsg = `✗ ${err?.error?.detail || err?.message || 'Update failed'}`;
        this.savingExternalId = false;
      },
    });
  }

  /** True once all 3 tradefinance headers are set on the dossier. */
  get hasTradeFinanceHeaders(): boolean {
    return !!(this.dossier?.customer_key && this.dossier?.account_key && this.dossier?.from_country);
  }

  /** Copy saved dossier values into the editable inputs (on initial load only). */
  private syncTradeFinanceDrafts(): void {
    this.tfCustomerKey = this.dossier?.customer_key || '';
    this.tfAccountKey = this.dossier?.account_key || '';
    this.tfFromCountry = this.dossier?.from_country || '';
  }

  /** Persist the tradefinance downstream-header inputs to the dossier. */
  saveTradeFinanceHeaders(): void {
    if (!this.dossier) { return; }
    this.savingTfHeaders = true;
    this.tfHeadersMsg = '';
    this.dossierService.updateTradefinanceHeaders(this.dossierId, {
      customer_key: this.tfCustomerKey.trim() || null,
      account_key: this.tfAccountKey.trim() || null,
      from_country: this.tfFromCountry.trim().toUpperCase() || null,
    }).subscribe({
      next: (updated: Dossier) => {
        // Merge — same reason as saveExternalId: the PATCH response has no `uploads`.
        if (this.dossier) { this.dossier = { ...this.dossier, ...updated }; }
        this.syncTradeFinanceDrafts();
        this.savingTfHeaders = false;
      },
      error: (err: { error?: { detail?: string }; message?: string }) => {
        this.tfHeadersMsg = `✗ ${err?.error?.detail || err?.message || 'Save failed'}`;
        this.savingTfHeaders = false;
      },
    });
  }
}
